import type {
  AuthBootstrapInput,
  AuthBootstrapResult,
  AuthSessionState,
  AuthWebSocketTokenResult,
  CmmdUserIdentity,
  DesktopAuthState,
  DesktopEnvironmentBootstrapEnvelope,
} from "@cmmd-forge/contracts";

import {
  getPairingTokenFromUrl,
  stripPairingTokenFromUrl as stripPairingTokenUrl,
} from "../../pairingUrl";

import { readPrimaryEnvironmentTarget, resolvePrimaryEnvironmentHttpUrl } from "./target";
import { buildPrimaryFetchInit } from "./fetchInit";
import { buildPrimaryTransportConfig, CMMD_AUTH_ERROR_CODES } from "./workIntake";
import { logoutPrimaryHttpSession } from "./logout";
import { captureWebError, setWebSentryUser } from "../../sentry";
import { identifyPosthogUser } from "../../observability/posthog";
import { cmmdApiClient, CmmdApiError } from "../cmmdApi";
import { request } from "../cmmdApi/transport";
import { isCloudBearerAuthFailure } from "../../lib/cloudAuthFailure";
import { setCloudAuthFailure } from "../../rpc/wsConnectionState";
import { hydrateRuntimeBridgeBootstrapEnvelope } from "../runtimeBridge";
import { BootstrapHttpError, isBootstrapHttpError } from "./bootstrapError";
import { readJsonResponse } from "./jsonResponse";
import { startGuardedAuthGate } from "./authGateRetry";
import {
  clearAuthExpiredReason,
  peekStoredAuthExpiredReason,
  redirectToForgeLoginAfterAuthExpired,
  renewForgeCloudSession,
  resolveAuthRedirectReasonMessage,
  storeAuthRedirectReason,
  type ForgeAuthRedirectReason,
} from "../../lib/authExpiredRedirect";
import {
  resolvePrimaryAccountIdentityFromSession,
  type PrimaryAccountIdentity,
} from "./authIdentityResolver";
import { __resetOriginSessionForTests } from "./authOriginSession";
export { BootstrapHttpError } from "./bootstrapError";
export { readJsonResponse } from "./jsonResponse";
export type { PrimaryAccountIdentity } from "./authIdentityResolver";
export { fetchCurrentOriginAccountIdentity } from "./authOriginSession";

export interface ServerCommandTokenOptions {
  readonly refreshToken?: string | null | undefined;
  readonly mcpToken?: string | null | undefined;
}

type ServerAuthGateState =
  | { status: "authenticated" }
  | {
      status: "requires-auth";
      auth: AuthSessionState["auth"];
      errorMessage?: string;
    };

export { buildPrimaryFetchInit } from "./fetchInit";
export { fetchPrimaryCmmdWorkIntake } from "./workIntake";

let bootstrapPromise: Promise<ServerAuthGateState> | null = null;
let resolvedAuthenticatedGateState: ServerAuthGateState | null = null;
let desktopAuthStateUnsubscribe: (() => void) | null = null;
let refreshGatePromise: Promise<ServerAuthGateState> | null = null;
const localWebBootstrapCredentials = new Set<string>();
const LOGGED_OUT_LOCAL_BOOTSTRAP_CREDENTIALS_KEY =
  "cmmd-forge.loggedOutLocalBootstrapCredentials.v1";

function identifyCmmdIdentityForWeb(identity: CmmdUserIdentity): void {
  const displayName = `${identity.firstName ?? ""} ${identity.lastName ?? ""}`.trim();
  setWebSentryUser({
    id: identity.id,
    email: identity.email,
    ...(displayName.length > 0 ? { username: displayName } : {}),
  });
  // PostHog identification is a no-op when PostHog hasn't initialized yet;
  // anonymous flag evaluation handles that brief window.
  identifyPosthogUser({
    cmmdUserId: identity.id,
    ...(identity.email ? { email: identity.email } : {}),
    ...(displayName.length > 0 ? { displayName } : {}),
  });
}

function ensureDesktopAuthStateSubscription(): void {
  if (typeof window === "undefined") {
    return;
  }
  if (desktopAuthStateUnsubscribe) {
    return;
  }
  const subscribe = window.desktopBridge?.desktopAuth?.onStateChange;
  if (!subscribe) {
    return;
  }
  desktopAuthStateUnsubscribe = subscribe((state) => {
    bootstrapPromise = null;
    resolvedAuthenticatedGateState = null;
    refreshGatePromise = null;
    if (state.sessionLifecycle.status !== "ready") {
      setWebSentryUser(null);
    }
    if (
      state.sessionLifecycle.status === "expired" ||
      (state.sessionLifecycle.status === "signed-out" &&
        state.sessionLifecycle.reason === "refresh-revoked")
    ) {
      void redirectToForgeLoginAfterAuthExpired().catch((error) => {
        captureWebError(error, { route: "desktop-auth-session-expired" });
      });
    }
  });
}

export function peekPairingTokenFromUrl(): string | null {
  return getPairingTokenFromUrl(new URL(window.location.href));
}

export function stripPairingTokenFromCurrentUrl() {
  const url = new URL(window.location.href);
  const next = stripPairingTokenUrl(url);
  if (next.toString() === url.toString()) {
    return;
  }
  window.history.replaceState({}, document.title, next.toString());
}

export function takePairingTokenFromUrl(): string | null {
  const token = peekPairingTokenFromUrl();
  if (!token) {
    return null;
  }
  stripPairingTokenFromCurrentUrl();
  return token;
}

export async function fetchSessionState(): Promise<AuthSessionState> {
  return retryTransientBootstrap(async () => {
    const endpointUrl = resolvePrimaryEnvironmentHttpUrl("/api/auth/session");
    const readSession = async (): Promise<AuthSessionState> => {
      const response = await fetch(endpointUrl, buildPrimaryFetchInit({ credentials: "include" }));
      if (!response.ok) {
        throw new BootstrapHttpError({
          message: `Failed to load server auth session state (${response.status}).`,
          status: response.status,
        });
      }
      return readJsonResponse<AuthSessionState>(response, endpointUrl);
    };
    const session = await readSession();
    const cloudOwnerSession =
      !session.authenticated &&
      session.auth.sessionMethods.length === 1 &&
      session.auth.sessionMethods[0] === "proxy-asserted" &&
      typeof window !== "undefined" &&
      !window.desktopBridge &&
      endpointUrl.startsWith("https://");
    if (cloudOwnerSession && (await renewForgeCloudSession())) {
      return readSession();
    }
    return session;
  });
}

async function readErrorMessage(response: Response, fallbackMessage: string): Promise<string> {
  const text = await response.text();
  return text || fallbackMessage;
}

async function exchangeBootstrapCredential(credential: string): Promise<AuthBootstrapResult> {
  return retryTransientBootstrap(async () => {
    const payload: AuthBootstrapInput = { credential };
    const endpointUrl = resolvePrimaryEnvironmentHttpUrl("/api/auth/bootstrap");
    const response = await fetch(
      endpointUrl,
      buildPrimaryFetchInit({
        body: JSON.stringify(payload),
        credentials: "include",
        headers: {
          "content-type": "application/json",
        },
        method: "POST",
      }),
    );

    if (!response.ok) {
      const message = await response.text();
      throw new BootstrapHttpError({
        message: message || `Failed to bootstrap auth session (${response.status}).`,
        status: response.status,
      });
    }

    return readJsonResponse<AuthBootstrapResult>(response, endpointUrl);
  });
}

function resolveLocalWebAuthUrl(pathname: string): string {
  const url = new URL(pathname, window.location.origin);
  return url.toString();
}

function readLocalBootstrapCredential(
  envelope: DesktopEnvironmentBootstrapEnvelope | null,
): string | null {
  const localBootstrap = envelope?.primaryMode === "cloud" ? null : envelope;
  const credential = localBootstrap?.bootstrapToken?.trim();
  return credential && credential.length > 0 ? credential : null;
}

function readCurrentLocalBootstrapCredential(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  return readLocalBootstrapCredential(
    window.desktopBridge?.getLocalEnvironmentBootstrap?.() ?? null,
  );
}

function isExplicitSessionExpiredLoginRoute(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  // Gate check: only a stored one-shot from a real redirect should block local
  // desktop bootstrap. A legacy URL query may be stale after re-auth and is
  // consumed later by the login surface when the session is still unauthenticated.
  return peekStoredAuthExpiredReason() === "session-expired";
}

function readLoggedOutLocalBootstrapCredentials(): ReadonlySet<string> {
  if (typeof window === "undefined") {
    return new Set();
  }
  try {
    const raw = window.sessionStorage.getItem(LOGGED_OUT_LOCAL_BOOTSTRAP_CREDENTIALS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) {
      return new Set();
    }
    return new Set(parsed.filter((value): value is string => typeof value === "string"));
  } catch {
    return new Set();
  }
}

function writeLoggedOutLocalBootstrapCredentials(credentials: ReadonlySet<string>): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    if (credentials.size === 0) {
      window.sessionStorage.removeItem(LOGGED_OUT_LOCAL_BOOTSTRAP_CREDENTIALS_KEY);
      return;
    }
    window.sessionStorage.setItem(
      LOGGED_OUT_LOCAL_BOOTSTRAP_CREDENTIALS_KEY,
      JSON.stringify([...credentials]),
    );
  } catch {
    // Best-effort client hint. Server logout still revokes the active session.
  }
}

function markCurrentLocalBootstrapCredentialLoggedOut(): void {
  const credential = readCurrentLocalBootstrapCredential();
  if (!credential) {
    return;
  }
  const credentials = new Set(readLoggedOutLocalBootstrapCredentials());
  credentials.add(credential);
  writeLoggedOutLocalBootstrapCredentials(credentials);
}

function isLocalBootstrapCredentialLoggedOut(credential: string): boolean {
  return readLoggedOutLocalBootstrapCredentials().has(credential);
}

export function clearLoggedOutLocalBootstrapCredentialForCurrentEnvironment(): void {
  const credential = readCurrentLocalBootstrapCredential();
  if (!credential) {
    writeLoggedOutLocalBootstrapCredentials(new Set());
    return;
  }
  const credentials = new Set(readLoggedOutLocalBootstrapCredentials());
  credentials.delete(credential);
  writeLoggedOutLocalBootstrapCredentials(credentials);
}

async function fetchLocalWebSessionState(): Promise<AuthSessionState | null> {
  const endpoint = resolveLocalWebAuthUrl("/api/auth/session");
  const response = await fetch(endpoint, { credentials: "include" });
  if (!response.ok) {
    return null;
  }
  return readJsonResponse<AuthSessionState>(response, endpoint);
}

async function bootstrapLocalWebOwnerSession(
  envelope: DesktopEnvironmentBootstrapEnvelope | null,
): Promise<void> {
  if (typeof window === "undefined") {
    return;
  }
  const credential = readLocalBootstrapCredential(envelope);
  if (
    !credential ||
    isExplicitSessionExpiredLoginRoute() ||
    localWebBootstrapCredentials.has(credential) ||
    isLocalBootstrapCredentialLoggedOut(credential)
  ) {
    return;
  }

  const currentSession = await fetchLocalWebSessionState().catch(() => null);
  if (currentSession?.authenticated) {
    localWebBootstrapCredentials.add(credential);
    return;
  }

  const endpoint = resolveLocalWebAuthUrl("/api/auth/bootstrap");
  const response = await fetch(endpoint, {
    body: JSON.stringify({ credential } satisfies AuthBootstrapInput),
    credentials: "include",
    headers: { "content-type": "application/json" },
    method: "POST",
  });
  if (!response.ok) {
    if (response.status === 401) {
      return;
    }
    const message = await response.text();
    throw new BootstrapHttpError({
      message: message || `Failed to bootstrap local web owner session (${response.status}).`,
      status: response.status,
    });
  }
  await readJsonResponse<AuthBootstrapResult>(response, endpoint);
  localWebBootstrapCredentials.add(credential);
}

async function fetchSessionStateWithDesktopBearerFallback(): Promise<AuthSessionState> {
  const session = await fetchSessionState();
  if (session.authenticated) {
    return session;
  }

  const target = readPrimaryEnvironmentTarget();
  if (target?.source !== "desktop-managed" || !target.bearerToken) {
    return session;
  }

  const currentOriginSession = await fetchLocalWebSessionState().catch(() => null);
  return currentOriginSession?.authenticated ? currentOriginSession : session;
}

const TRANSIENT_BOOTSTRAP_STATUS_CODES = new Set([404, 502, 503, 504]);
const BOOTSTRAP_RETRY_TIMEOUT_MS = 15_000;
const BOOTSTRAP_RETRY_STEP_MS = 500;

/**
 * Check whether a thrown error represents an expired cloud bearer session.
 * Scoped strictly to `paired-bearer` source — cookie/browser sessions are
 * unaffected.
 */
function interceptHttpCloudBearerAuthFailure(error: unknown): void {
  const target = readPrimaryEnvironmentTarget();
  if (target?.source !== "paired-bearer") return;
  if (isCloudBearerAuthFailure(error)) {
    setCloudAuthFailure("http");
  }
}

export async function retryTransientBootstrap<T>(operation: () => Promise<T>): Promise<T> {
  const startedAt = Date.now();
  while (true) {
    try {
      return await operation();
    } catch (error) {
      interceptHttpCloudBearerAuthFailure(error);

      if (!isTransientBootstrapError(error)) {
        throw error;
      }

      if (Date.now() - startedAt >= BOOTSTRAP_RETRY_TIMEOUT_MS) {
        captureWebError(error, { route: "bootstrap-retry-exhausted" });
        throw error;
      }

      await waitForBootstrapRetry(BOOTSTRAP_RETRY_STEP_MS);
    }
  }
}

function waitForBootstrapRetry(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
}

function isTransientBootstrapError(error: unknown): boolean {
  if (isBootstrapHttpError(error)) {
    return TRANSIENT_BOOTSTRAP_STATUS_CODES.has(error.status);
  }

  if (error instanceof TypeError) {
    return true;
  }

  return error instanceof DOMException && error.name === "AbortError";
}

function resolveDesktopCmmdAuthGateFailure(
  state: DesktopAuthState,
  serverIdentity: CmmdUserIdentity,
): ForgeAuthRedirectReason | null {
  if (state.sessionLifecycle.status !== "ready") {
    return "desktop-session-required";
  }

  if (
    state.sessionLifecycle.principalId !== serverIdentity.id ||
    state.cmmd.identity?.id !== serverIdentity.id
  ) {
    return "desktop-principal-mismatch";
  }

  return null;
}

async function readDesktopCmmdAuthGateFailure(
  serverIdentity: CmmdUserIdentity,
): Promise<ForgeAuthRedirectReason | null> {
  if (typeof window === "undefined" || !window.desktopBridge) {
    return null;
  }

  const getState = window.desktopBridge.desktopAuth?.getState;
  if (!getState) {
    return "desktop-state-unavailable";
  }

  try {
    return resolveDesktopCmmdAuthGateFailure(await getState(), serverIdentity);
  } catch (error) {
    captureWebError(error, { route: "desktop-auth-gate-state" });
    return "desktop-state-unavailable";
  }
}

async function rejectSplitDesktopAuthSession(
  currentSession: AuthSessionState,
  reason: ForgeAuthRedirectReason,
): Promise<ServerAuthGateState> {
  setWebSentryUser(null);
  storeAuthRedirectReason(reason);
  await logoutPrimaryHttpSession().catch((error) => {
    captureWebError(error, { route: "desktop-auth-gate-logout" });
  });
  return {
    status: "requires-auth",
    auth: currentSession.auth,
    errorMessage:
      resolveAuthRedirectReasonMessage(reason) ?? "Forge Desktop requires CMMD authentication.",
  };
}

async function bootstrapServerAuth(): Promise<ServerAuthGateState> {
  const currentSession = await fetchSessionStateWithDesktopBearerFallback();
  if (currentSession.authenticated) {
    if (currentSession.cmmdIdentity) {
      const desktopGateFailure = await readDesktopCmmdAuthGateFailure(currentSession.cmmdIdentity);
      if (desktopGateFailure) {
        clearAuthExpiredReason();
        return rejectSplitDesktopAuthSession(currentSession, desktopGateFailure);
      }
      clearAuthExpiredReason();
      identifyCmmdIdentityForWeb(currentSession.cmmdIdentity);
      return { status: "authenticated" };
    }
    // A native token is not evidence that this Forge server session belongs to
    // the same CMMD principal. The server-owned CMMD identity is the gate.
    return {
      status: "requires-auth",
      auth: currentSession.auth,
    };
  }

  return {
    status: "requires-auth",
    auth: currentSession.auth,
  };
}

export async function submitServerAuthCredential(credential: string): Promise<void> {
  const trimmedCredential = credential.trim();
  if (!trimmedCredential) {
    throw new Error("Enter a pairing token to continue.");
  }

  resolvedAuthenticatedGateState = null;
  clearLoggedOutLocalBootstrapCredentialForCurrentEnvironment();
  await exchangeBootstrapCredential(trimmedCredential);
  bootstrapPromise = null;
  stripPairingTokenFromCurrentUrl();
}

/**
 * Drop every cached "not authenticated" answer after a lane establishes a
 * session. The gate state and the in-flight bootstrap promise both memoize a
 * pre-login world; leaving either in place sends the next caller back to
 * /login with a session cookie already in hand.
 */
export function markServerSessionEstablished(): void {
  clearLoggedOutLocalBootstrapCredentialForCurrentEnvironment();
  resolvedAuthenticatedGateState = null;
  bootstrapPromise = null;
}

export async function submitServerCommandAccessToken(
  accessToken: string,
  user?: unknown,
  options?: ServerCommandTokenOptions,
): Promise<void> {
  const trimmedAccessToken = accessToken.trim();
  if (!trimmedAccessToken) {
    throw new Error("CMMD (Command) OAuth did not return an access token.");
  }
  const refreshToken = options?.refreshToken?.trim();
  const mcpToken = options?.mcpToken?.trim();

  const endpointUrl = resolvePrimaryEnvironmentHttpUrl("/api/auth/bootstrap/command");
  const response = await fetch(
    endpointUrl,
    buildPrimaryFetchInit({
      body: JSON.stringify({
        accessToken: trimmedAccessToken,
        ...(refreshToken ? { refreshToken } : {}),
        ...(mcpToken ? { mcpToken } : {}),
        ...(user !== undefined ? { user } : {}),
      }),
      credentials: "include",
      headers: {
        "content-type": "application/json",
      },
      method: "POST",
    }),
  );

  if (!response.ok) {
    const message = await response.text();
    throw new BootstrapHttpError({
      message: message || `Failed to bootstrap with CMMD OAuth token (${response.status}).`,
      status: response.status,
    });
  }

  await readJsonResponse<AuthBootstrapResult>(response, endpointUrl);
  markServerSessionEstablished();
}

async function issuePrimaryWebSocketToken(): Promise<AuthWebSocketTokenResult> {
  const endpointUrl = resolvePrimaryEnvironmentHttpUrl("/api/auth/ws-token");
  const response = await fetch(
    endpointUrl,
    buildPrimaryFetchInit({ credentials: "include", method: "POST" }),
  );

  if (!response.ok) {
    const message = await response.text();
    throw new BootstrapHttpError({
      message: message || `Failed to issue websocket token (${response.status}).`,
      status: response.status,
    });
  }

  return readJsonResponse<AuthWebSocketTokenResult>(response, endpointUrl);
}

export async function resolvePrimaryWebSocketConnectionUrl(wsBaseUrl: string): Promise<string> {
  const issued = await issuePrimaryWebSocketToken();
  const url = new URL(wsBaseUrl, window.location.origin);
  url.searchParams.set("wsToken", issued.token);
  return url.toString();
}

export async function fetchPrimaryAccountIdentity(): Promise<PrimaryAccountIdentity> {
  const session = await fetchSessionState();
  return resolvePrimaryAccountIdentityFromSession(session);
}

export interface UpdatePrimaryCmmdTaskInput {
  readonly taskId: string;
  readonly title?: string;
  readonly description?: string | null;
  readonly status?: string | null;
  readonly priority?: string | null;
  readonly dueDate?: string | null;
  readonly assigneeId?: string | null;
  readonly labels?: ReadonlyArray<string>;
  readonly comment?: string;
}

async function postPrimaryCmmdTaskUpdateFromPath(
  pathname: string,
  input: UpdatePrimaryCmmdTaskInput,
): Promise<{ success: boolean; details?: unknown }> {
  try {
    return await request<{ success: boolean; details?: unknown }>(
      { method: "POST", path: pathname, body: input, allowRetry: false },
      buildPrimaryTransportConfig(),
    );
  } catch (err) {
    if (
      err instanceof CmmdApiError &&
      err.kind !== "auth" &&
      err.code !== null &&
      CMMD_AUTH_ERROR_CODES.has(err.code)
    ) {
      throw new CmmdApiError({
        kind: "auth",
        message: err.message,
        status: err.status ?? 401,
        code: err.code,
      });
    }
    throw err;
  }
}

export async function updatePrimaryCmmdTask(
  input: UpdatePrimaryCmmdTaskInput,
): Promise<{ success: boolean; details?: unknown }> {
  try {
    return await postPrimaryCmmdTaskUpdateFromPath("/api/cmmd/tasks/update", input);
  } catch (error) {
    if (error instanceof CmmdApiError && (error.kind === "auth" || error.kind === "permission")) {
      throw error;
    }
    if (error instanceof CmmdApiError || error instanceof Error) {
      return postPrimaryCmmdTaskUpdateFromPath("/api/command/tasks/update", input);
    }
    throw error;
  }
}

export interface CreateBrainDocInput {
  readonly title: string;
  readonly content: string;
  readonly entityId?: string;
  readonly tags?: ReadonlyArray<string>;
}

export async function createPrimaryBrainDoc(
  input: CreateBrainDocInput,
): Promise<{ success: boolean; doc: unknown }> {
  const doc = await cmmdApiClient.createBrainDoc(input);
  return { success: true, doc: doc as unknown };
}

async function logoutDesktopCmmdSession(): Promise<void> {
  if (typeof window === "undefined") {
    return;
  }
  await window.desktopBridge?.desktopAuth?.logoutCloud?.();
}

export async function logoutPrimarySession(): Promise<void> {
  const logoutResults = await Promise.allSettled([
    logoutPrimaryHttpSession(),
    logoutDesktopCmmdSession(),
  ]);
  setWebSentryUser(null);
  markCurrentLocalBootstrapCredentialLoggedOut();
  bootstrapPromise = null;
  resolvedAuthenticatedGateState = null;

  const failure = logoutResults.find(
    (result): result is PromiseRejectedResult => result.status === "rejected",
  );
  if (failure) {
    throw failure.reason;
  }
}

export async function revokeOtherServerClientSessions(): Promise<number> {
  const endpointUrl = resolvePrimaryEnvironmentHttpUrl("/api/auth/clients/revoke-others");
  const response = await fetch(
    endpointUrl,
    buildPrimaryFetchInit({ credentials: "include", method: "POST" }),
  );

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(
        response,
        `Failed to revoke other client sessions (${response.status}).`,
      ),
    );
  }

  const result = await readJsonResponse<{ revokedCount?: number }>(response, endpointUrl);
  return result.revokedCount ?? 0;
}

export async function resolveInitialServerAuthGateState(): Promise<ServerAuthGateState> {
  ensureDesktopAuthStateSubscription();
  if (resolvedAuthenticatedGateState?.status === "authenticated") {
    clearAuthExpiredReason();
    return resolvedAuthenticatedGateState;
  }

  if (bootstrapPromise) {
    return bootstrapPromise;
  }

  return startGuardedAuthGate({
    start: runServerAuthBootstrap,
    getSlot: () => bootstrapPromise,
    setSlot: (promise) => {
      bootstrapPromise = promise;
    },
    onSettled: (result) => {
      if (result.status === "authenticated") resolvedAuthenticatedGateState = result;
    },
    retry: resolveInitialServerAuthGateState,
  });
}

async function runServerAuthBootstrap(): Promise<ServerAuthGateState> {
  return hydrateRuntimeBridgeBootstrapEnvelope()
    .then((envelope) => bootstrapLocalWebOwnerSession(envelope))
    .then(() => bootstrapServerAuth());
}

/**
 * A desktop state change mid-refresh interrupts the in-flight request. Retry
 * re-derives the answer from current state instead of handing a caller
 * awaiting this exact promise (see LoginAuthSessionRecheckCoordinator) a
 * stale result: an authenticated result could describe a session that has
 * since signed out, a requires-auth result one that has since signed in, and
 * a rejection one that no longer applies at all.
 */
export function refreshServerAuthGateState(): Promise<ServerAuthGateState> {
  ensureDesktopAuthStateSubscription();
  if (refreshGatePromise) return refreshGatePromise;
  bootstrapPromise = null;
  resolvedAuthenticatedGateState = null;
  return startGuardedAuthGate({
    start: runServerAuthBootstrap,
    getSlot: () => refreshGatePromise,
    setSlot: (promise) => {
      refreshGatePromise = promise;
    },
    onSettled: (result) => {
      if (result.status === "authenticated") {
        resolvedAuthenticatedGateState = result;
        clearAuthExpiredReason();
      }
    },
    retry: refreshServerAuthGateState,
  });
}

export function __resetServerAuthBootstrapForTests() {
  desktopAuthStateUnsubscribe?.();
  desktopAuthStateUnsubscribe = null;
  bootstrapPromise = null;
  resolvedAuthenticatedGateState = null;
  refreshGatePromise = null;
  __resetOriginSessionForTests();
  localWebBootstrapCredentials.clear();
}
